// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../../../../components/ui/Button";
import { getProjects, getProject } from "../../../../../../api";
import { getStoryById, saveStory } from "../../../../../../api/museum";
import Dropdown from "../../../../../../components/common/Dropdown";
import HorizontalSeparator from "../../../../../../components/common/HorizontalSeparator";
import RadioButton from "../../../../../../components/common/RadioButton";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import TextInput from "../../../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../../../context/useGlobalState";
import { StoryTextProps, RouteParams, DropdownOption } from "../../../../../../types/screensTypes";
import { idToLanguage } from "../../../../../../utils/idtolanguage";
import activeRadio from "../../../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../../../assets/images/radio_inactive.png";
import stepStyles from "../../../createStep.module.css";
import storyEdit from "./StoryEdit.module.css";

const StoryText: React.FC<StoryTextProps> = ({ navigateToNext }) => {
    const { storyId, planId, language } = useParams<RouteParams>(); // Extract URL parameters
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();
    const { trackData } = useTrackCreation();

    // State for the story text and input mode
    const [text, setText] = useState<string>("");
    const [inputMode, setInputMode] = useState<"manual" | "project">("manual");

    // States for "project mode"
    const [projects, setProjects] = useState<DropdownOption[]>([]);
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [projectData, setProjectData] = useState<any>(null);

    const languagesAIOptions: DropdownOption[] = data?.languagesAI?.map((language) => ({
        value: language.code,
        label: language.label,
    }));

    // Derive story text options from project data (if available)
    const storyTextOptions: DropdownOption[] =
        projectData?.audioAI
            ?.filter((item: any) => item.language === selectedContentLanguage)
            .map((item: any) => ({
                value: item.text,
                label: item.name,
            })) || [];

    // Fetch projects for selection (project mode)
    const fetchProjects = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const response = await getProjects({ token });
            if (response) {
                const projectsDropdown = response.map((project: any) => ({
                    label: project.name,
                    value: project.id,
                }));
                setProjects(projectsDropdown);
            }
        } catch (err) {
            console.error("Failed to fetch projects:", err);
        }
    };

    // Fetch details for a selected project to retrieve its text options
    const fetchProject = async (id: string): Promise<void> => {
        if (!id) return;
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await getProject({ token, projectId: id });
        if (response && response.id) {
            setProjectData(response);
        }
    };

    // Fetch existing story text from API
    const getStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !storyId || !language) return;
        const storyData = await getStoryById({
            token,
            trackId,
            clickableZoneId: storyId,
            language,
        });

        if (storyData) {
            const localizedData = storyData.clickableZonesLocalized;
            if (localizedData && localizedData.length > 0) {
                const longDescription = localizedData[0].longDescription;
                setText(longDescription);
            }
        }
    };

    // Save story using current text (from manual or project mode)
    const saveStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !planId) return;
        const tempWpt = await saveStory({
            token,
            trackId,
            clickableZoneId: storyId,
            subplanId: planId,
            language,
            text,
        });
        if (tempWpt && tempWpt.clickableZoneId && language) {
            const id: string = tempWpt.clickableZoneId;
            navigateToNext(id, language, planId);
        }
    };

    useEffect(() => {
        if (storyId && storyId !== "") {
            getStoryAPI();
        }
    }, [storyId, language]);

    // For project mode, fetch projects on mount
    useEffect(() => {
        fetchProjects();
    }, []);

    // Fetch project details when a project is selected
    useEffect(() => {
        if (selectedProject && selectedProject !== "") {
            fetchProject(selectedProject);
        }
    }, [selectedProject]);

    return (
        <div>
            <TitleSubtitle
                title={t("storyText") + idToLanguage(language || "", data?.languagesAI, t)}
                subtitle={""}
                layout="step"
            />
            <HorizontalSeparator />

            <div className={`text-edit-section ${storyEdit.textArea}`}>
                <TextInput
                    placeholder={t("pleaseInsertYourText")}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    size={"big"}
                    numberOfLines={6}
                />
            </div>
            <HorizontalSeparator></HorizontalSeparator>

            {/* Input mode selection */}
            <div className="radio-text-bold" style={{ marginTop: "20px" }}>
                <RadioButton
                    label={t("addFromYourProjects")}
                    checked={inputMode === "project"}
                    onChange={() => setInputMode("project")}
                    activeImage={activeRadio}
                    inactiveImage={inactiveRadio}
                    center={true}
                />
            </div>

            {inputMode === "project" && (
                <div className="dropdown-row">
                    <Dropdown
                        label=""
                        value={selectedProject}
                        options={projects}
                        onChange={(value: string) => setSelectedProject(value)}
                        placeholder={t("selectProject")}
                        loading={false}
                        error={false}
                    />
                    <Dropdown
                        label=""
                        value={selectedContentLanguage}
                        options={languagesAIOptions}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("selectALanguage")}
                    />
                    {selectedProject && selectedContentLanguage && (
                        <Dropdown
                            label=""
                            value={text}
                            options={storyTextOptions}
                            onChange={setText}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("selectAText")}
                        />
                    )}
                </div>
            )}

            <div className={stepStyles.stepButton}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={async () => {
                        await saveStoryAPI();
                    }}
                />
            </div>
        </div>
    );
};

export default StoryText;
